// frontend/src/modules/payroll/components/PayslipPdfPreview.tsx
//
// Embedded payslip PDF previewer. Mirrors the canonical invoicing
// PdfPreviewPanel — fetches the PDF as a Blob with the auth token, builds
// an Object URL, and renders inline in an iframe. Provides a toolbar
// (download + open in new tab) and a skeleton / error fallback.
import * as React from 'react';
import { Download, ExternalLink, FileWarning, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import { getToken } from '../../../core/api-client';
import { VISUAL } from '../../../design-system/visual';

interface Props {
  payslipId: string | null;
  /** Label shown in the toolbar — typically employee name + period. */
  label?: string;
}

export function PayslipPdfPreview({ payslipId, label }: Props) {
  const [url, setUrl] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [retryTick, setRetryTick] = React.useState(0);

  React.useEffect(() => {
    if (!payslipId) { setUrl(null); setError(null); return; }

    let cancelled = false;
    let revokeUrl: string | null = null;
    setLoading(true);
    setError(null);

    const token = getToken();
    fetch(`/api/payroll/payslips/${payslipId}/pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        const ct = res.headers.get('content-type') ?? '';
        const bytes = await res.arrayBuffer();
        return new Blob([bytes], { type: ct.includes('pdf') ? ct : 'application/pdf' });
      })
      .then((blob) => {
        if (cancelled) return;
        const u = URL.createObjectURL(blob);
        revokeUrl = u;
        setUrl(u);
      })
      .catch((err) => { if (!cancelled) setError(err.message || 'Unknown error'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => {
      cancelled = true;
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [payslipId, retryTick]);

  if (!payslipId) {
    return (
      <div className="h-full flex items-center justify-center p-8 text-center">
        <div className="max-w-[220px]">
          <div className="mx-auto mb-4 h-12 w-12 rounded-2xl bg-muted/60 ring-1 ring-border/50 flex items-center justify-center">
            <FileText className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
          </div>
          <p className="text-[13px] text-muted-foreground leading-relaxed">
            Select a payslip to preview its PDF here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border/40">
        <div className="min-w-0 flex items-center gap-2">
          <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" strokeWidth={1.8} />
          <span className="text-[12.5px] font-semibold text-foreground truncate">
            {label ?? 'Payslip PDF'}
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {url ? (
            <>
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                title="Open in new tab"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full ring-1 ring-border/60 text-muted-foreground hover:text-foreground hover:ring-border/90 transition-colors"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <a
                href={url}
                download={`payslip-${payslipId}.pdf`}
                title="Download"
                className="inline-flex h-8 items-center gap-1.5 rounded-full bg-foreground px-3 text-[12px] font-semibold text-background"
              >
                <Download className="h-3 w-3" />
                Download
              </a>
            </>
          ) : null}
        </div>
      </div>

      <div className="flex-1 relative bg-muted/30">
        {loading && !error && <PdfSkeleton />}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center p-8 text-center">
            <div className="max-w-[260px]">
              <div className="mx-auto mb-3 h-12 w-12 rounded-2xl bg-destructive/10 ring-1 ring-destructive/25 flex items-center justify-center">
                <FileWarning className="h-5 w-5 text-destructive" strokeWidth={1.6} />
              </div>
              <p className="text-[13.5px] font-semibold text-foreground">Payslip couldn't render</p>
              <p className="text-[12.5px] text-muted-foreground mt-1">{error}</p>
              <button
                type="button"
                onClick={() => setRetryTick((t) => t + 1)}
                className="mt-4 inline-flex h-8 items-center gap-1.5 rounded-full bg-foreground px-3.5 text-[12px] font-semibold text-background hover:opacity-95"
              >
                Try again
              </button>
            </div>
          </div>
        )}
        {url && !error && (
          <iframe
            src={`${url}#zoom=page-width&navpanes=0`}
            title="Payslip PDF"
            className="absolute inset-0 w-full h-full"
          />
        )}
      </div>
    </div>
  );
}

function PdfSkeleton() {
  return (
    <div className="absolute inset-0 flex items-start justify-center p-6">
      <motion.div
        className={[VISUAL.bento, 'w-full max-w-[360px] aspect-[3/4] overflow-hidden p-6'].join(' ')}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 120, damping: 22 }}
      >
        <div className="h-3 w-1/3 rounded bg-muted animate-pulse" />
        <div className="mt-2 h-2 w-1/4 rounded bg-muted/70 animate-pulse" />
        <div className="mt-8 space-y-2">
          {[0.95, 0.9, 0.85, 0.92, 0.6, 0.8, 0.7].map((w, i) => (
            <div
              key={i}
              className="h-2 rounded bg-muted/60 animate-pulse"
              style={{ width: `${Math.round(w * 100)}%`, animationDelay: `${i * 80}ms` }}
            />
          ))}
        </div>
        <div className="mt-8 grid grid-cols-3 gap-2">
          <div className="h-12 rounded-lg bg-muted/50 animate-pulse" />
          <div className="h-12 rounded-lg bg-muted/50 animate-pulse" style={{ animationDelay: '120ms' }} />
          <div className="h-12 rounded-lg bg-saffron/40 animate-pulse" style={{ animationDelay: '240ms' }} />
        </div>
      </motion.div>
    </div>
  );
}
